import React, { useRef, useCallback, useEffect, useState } from 'react';
import Spline from '@splinetool/react-spline';
import styles from './SpaceScene.module.css';

function SpaceScene({ onProbeEvent, onProbeLaunch, isAudioMuted }) {
  const splineRef = useRef(null);
  const audioRef = useRef(null);
  const lastClickTime = useRef(0);
  const CLICK_COOLDOWN = 8000; // 8 seconds in milliseconds
  const [isMobile, setIsMobile] = useState(false);

  // Detect ACTUAL mobile devices (not just narrow windows)
  useEffect(() => {
    const checkMobile = () => {
      // Check if it's actually a touch device
      const isTouchDevice = (
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        navigator.msMaxTouchPoints > 0
      );
      
      const isSmallScreen = window.matchMedia('(max-width: 768px)').matches;
      
      // Only treat as mobile if BOTH touch-capable AND small screen
      const actuallyMobile = isTouchDevice && isSmallScreen;
      
      setIsMobile(actuallyMobile);
    };

    // Initial check
    checkMobile();

    // Listen for resize events
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle Spline scene load
  const onLoad = useCallback((splineApp) => {
    splineRef.current = splineApp;
    console.log('Spline scene loaded successfully');
    
    // Start background audio when Spline scene loads
    if (audioRef.current) {
      audioRef.current.play().catch(error => {
        console.log('Audio autoplay prevented by browser:', error);
        // Audio will play when user interacts with the page
      });
    }
    
    // Try to listen for Spline events (if they exist in your scene)
    if (splineApp && splineApp.addEventListener) {
      console.log('🎯 Setting up Spline event listeners...');
      
      // Listen for custom events that might be triggered by your Spline scene
      // These are common event names used in Spline scenes
      const eventNames = [
        'mouseDown', 'mouseUp', 'click', 'hover', 'unhover', 'Hover',
        'MoonClick', 'moonClick', 'Moon1Click', 'Moon1Hover', 'moon1Click', 'moon1Hover',
        'probeTarget', 'targetLock', 'launchProbe', 'moonClick',
        'Moon 1 Click', 'Moon 1 Hover'
      ];
      
      eventNames.forEach(eventName => {
        splineApp.addEventListener(eventName, (e) => {
          console.log(`🎯 Spline event "${eventName}":`, e);
          
          // If this is a Moon 1 related event, trigger our probe
          if (eventName.toLowerCase().includes('moon1') || 
              eventName.toLowerCase().includes('moon 1') ||
              eventName.toLowerCase().includes('moonclick') ||
              eventName.toLowerCase().includes('probe') ||
              eventName.toLowerCase().includes('target') ||
              eventName.toLowerCase().includes('hover')) {
            
            console.log('🚀 Moon 1 event detected! Launching probe...');
            triggerProbeEvent();
          }
          
          // For any click event, check if MoonClick variable is set
          if (eventName === 'click') {
            console.log('🖱️ Click detected, checking MoonClick variable...');
            
            // Check MoonClick variable immediately after click using the working method
            setTimeout(() => {
              if (splineRef.current?.getVariable?.('MoonClick') === true) {
                const currentTime = Date.now();
                const timeSinceLastClick = currentTime - lastClickTime.current;
                
                if (timeSinceLastClick < CLICK_COOLDOWN) {
                  const remainingCooldown = ((CLICK_COOLDOWN - timeSinceLastClick) / 1000).toFixed(1);
                  console.log(`⏳ Probe launch on cooldown - ${remainingCooldown}s remaining`);
                  splineRef.current.setVariable?.('MoonClick', false); // Reset the click variable
                  return;
                }
                
                console.log('🎯 MoonClick detected via click! Launching probe...');
                lastClickTime.current = currentTime; // Update last click time
                splineRef.current.setVariable?.('MoonClick', false); // Reset
                
                // Toggle TriggerProbe variable to start animation
                const currentTriggerProbe = splineRef.current.getVariable('TriggerProbe') || false;
                const newTriggerProbe = !currentTriggerProbe;
                splineRef.current.setVariable?.('TriggerProbe', newTriggerProbe);
                console.log(`🎬 Set TriggerProbe to ${newTriggerProbe} - animation should ${newTriggerProbe ? 'start' : 'stop'}`);
                
                // Notify HUD about launch
                if (onProbeLaunch) {
                  onProbeLaunch();
                }
                
                triggerProbeEvent();
              }
            }, 50); // Small delay to let Spline update the variable
          }
        });
      });
      
      // Try to find objects in the scene and set up direct listeners
      try {
        const objects = splineApp.getAllObjects ? splineApp.getAllObjects() : [];
        console.log('🔍 Found objects in scene:', objects);
        
        // Look for Moon objects specifically and try to add click listeners
        objects.forEach(obj => {
          if (obj.name && obj.name.toLowerCase().includes('moon')) {
            console.log('🌙 Found moon object:', obj.name, obj);
            
            // Try to add click listener directly to Moon 1 objects
            if (obj.name === 'Moon 1' || obj.name === 'Moon 1 Sphere') {
              console.log('🎯 Setting up direct click listener for:', obj.name);
              
              // Try different ways to add click listeners
              if (obj.addEventListener) {
                obj.addEventListener('click', () => {
                  console.log('🚀 Direct Moon 1 click detected!');
                  triggerProbeEvent();
                });
              }
              
              if (obj.onClick) {
                obj.onClick = () => {
                  console.log('🚀 Direct Moon 1 onClick detected!');
                  triggerProbeEvent();
                };
              }
            }
          }
        });
      } catch (error) {
        console.log('⚠️ Could not get objects from scene:', error);
      }
    }
    
    // Helper function to trigger probe event
    const triggerProbeEvent = () => {
      const funnyAnalyses = [
        'Massive retardium deposits detected - perfect for diamond hands mining operations',
        'High concentrations of degenium found - ideal for moon farming protocols',
        'Rare retardium-degenium alloy discovered - HODL potential: MAXIMUM',
        'Surface rich in pure degenium ore - recommended for ape-level extraction',
        'Crystallized retardium formations detected - to the moon confirmed',
        'Degenium veins running deep - this is the way for space mining',
        'Unprecedented retardium purity levels - number go up technology detected',
        'Degenium-infused rock formations - perfect for building rocket fuel dreams'
      ];
      
      const probeData = {
        target: 'Moon 1',
        status: 'Analysis Complete',
        distance: `${Math.floor(Math.random() * 2000 + 500)} km`,
        signal: Math.random() > 0.3 ? 'Strong' : 'Weak',
        analysis: funnyAnalyses[Math.floor(Math.random() * funnyAnalyses.length)]
      };
      
      setTimeout(() => {
        console.log('🎯 Probe analysis complete!');
        if (onProbeEvent) {
          onProbeEvent(probeData);
        }
      }, 6000); // Changed to 6 seconds
    };
    
    // Monitor for variable changes in the Spline scene
    // Since you mentioned a boolean is set on click, let's try to detect that
    if (splineApp && splineApp.emitEvent) {
      // Try to set up a polling mechanism to check for state changes
      const checkForStateChanges = () => {
        try {
          // Check MoonClick variable using the working getVariable method
          if (splineApp.getVariable) {
            try {
              const moonClickValue = splineApp.getVariable('MoonClick');
              if (moonClickValue === true) {
                const currentTime = Date.now();
                const timeSinceLastClick = currentTime - lastClickTime.current;
                
                if (timeSinceLastClick < CLICK_COOLDOWN) {
                  const remainingCooldown = ((CLICK_COOLDOWN - timeSinceLastClick) / 1000).toFixed(1);
                  console.log(`⏳ Probe launch on cooldown - ${remainingCooldown}s remaining`);
                  splineApp.setVariable?.('MoonClick', false); // Reset the click variable
                  return;
                }
                
                console.log('🎯 Detected MoonClick = true via getVariable! Launching probe...');
                lastClickTime.current = currentTime; // Update last click time
                splineApp.setVariable?.('MoonClick', false); // Reset to prevent multiple triggers
                
                // Toggle TriggerProbe variable to start animation
                const currentTriggerProbe = splineApp.getVariable('TriggerProbe') || false;
                const newTriggerProbe = !currentTriggerProbe;
                splineApp.setVariable?.('TriggerProbe', newTriggerProbe);
                console.log(`🎬 Set TriggerProbe to ${newTriggerProbe} - animation should ${newTriggerProbe ? 'start' : 'stop'}`);
                
                // Notify HUD about launch
                if (onProbeLaunch) {
                  onProbeLaunch();
                }
                
                triggerProbeEvent();
              }
            } catch (error) {
              // Ignore errors
            }
          }
        } catch (error) {
          // Silently ignore errors in polling
        }
      };
      
      // Poll every 100ms for state changes
      const pollInterval = setInterval(checkForStateChanges, 100);
      
      // Clean up interval after 30 seconds to prevent memory leaks
      setTimeout(() => clearInterval(pollInterval), 30000);
    }
  }, [onProbeEvent, onProbeLaunch]);

  // Handle audio mute state
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.muted = isAudioMuted;
    }
  }, [isAudioMuted]);

  // Cleanup audio when component unmounts
  useEffect(() => {
    const audioElement = audioRef.current;
    return () => {
      if (audioElement) {
        audioElement.pause();
        audioElement.currentTime = 0;
      }
    };
  }, []);

  // Handle mouse/touch events in Spline scene
  const onSplineMouseDown = useCallback((e) => {
    console.log('🖱️ Mouse/touch down event detected!', e);
    
    // Try to start audio on first user interaction (in case autoplay was blocked)
    if (audioRef.current && audioRef.current.paused) {
      audioRef.current.play().catch(error => {
        console.log('Could not start audio on interaction:', error);
      });
    }
    
    if (!e.target) {
      console.log('❌ No target found in event');
      return;
    }
    
    if (!e.target.name) {
      console.log('❌ Target has no name property:', e.target);
      return;
    }
    
    const objectName = e.target.name;
    console.log('✅ Clicked/tapped object:', objectName);
    
    // Check if clicked object is "Moon 1" (also try variations)
    if (objectName === 'Moon 1' || objectName === 'Moon1' || objectName.includes('Moon')) {
      const currentTime = Date.now();
      const timeSinceLastClick = currentTime - lastClickTime.current;
      
      if (timeSinceLastClick < CLICK_COOLDOWN) {
        const remainingCooldown = ((CLICK_COOLDOWN - timeSinceLastClick) / 1000).toFixed(1);
        console.log(`⏳ Probe launch on cooldown - ${remainingCooldown}s remaining`);
        return;
      }
      
      // Simulate probe launch and completion
      console.log(`Launching probe to ${objectName}...`);
      lastClickTime.current = currentTime; // Update last click time
      
      // Generate probe data for Moon 1 with funny crypto mineral analysis
      const funnyAnalyses = [
        'Massive retardium deposits detected - perfect for diamond hands mining operations',
        'High concentrations of degenium found - ideal for moon farming protocols',
        'Rare retardium-degenium alloy discovered - HODL potential: MAXIMUM',
        'Surface rich in pure degenium ore - recommended for ape-level extraction',
        'Crystallized retardium formations detected - to the moon confirmed',
        'Degenium veins running deep - this is the way for space mining',
        'Unprecedented retardium purity levels - number go up technology detected',
        'Degenium-infused rock formations - perfect for building rocket fuel dreams'
      ];
      
      const probeData = {
        target: objectName,
        status: 'Analysis Complete',
        distance: `${Math.floor(Math.random() * 2000 + 500)} km`,
        signal: Math.random() > 0.3 ? 'Strong' : 'Weak',
        analysis: funnyAnalyses[Math.floor(Math.random() * funnyAnalyses.length)]
      };
      
      // Toggle TriggerProbe variable to start animation
      if (splineRef.current?.setVariable) {
        const currentTriggerProbe = splineRef.current.getVariable('TriggerProbe') || false;
        const newTriggerProbe = !currentTriggerProbe;
        splineRef.current.setVariable('TriggerProbe', newTriggerProbe);
        console.log(`🎬 Set TriggerProbe to ${newTriggerProbe} - animation should ${newTriggerProbe ? 'start' : 'stop'}`);
      }
      
      // Notify HUD about launch
      if (onProbeLaunch) {
        onProbeLaunch();
      }
      
      // Simulate probe travel time (6 seconds to match animation)
      setTimeout(() => {
        console.log(`Probe reached ${objectName} - analysis complete!`);
        if (onProbeEvent) {
          onProbeEvent(probeData);
        }
      }, 6000);
    }
  }, [onProbeEvent, onProbeLaunch]);

  // Handle touch events specifically for mobile
  useEffect(() => {
    if (!isMobile) return;

    const handleTouchStart = (e) => {
      // Prevent default touch behaviors that might interfere with Spline interaction
      // But allow Spline to handle its own touch events
      console.log('👆 Touch start detected on mobile');
      
      // Start audio on touch if not playing
      if (audioRef.current && audioRef.current.paused) {
        audioRef.current.play().catch(error => {
          console.log('Could not start audio on touch:', error);
        });
      }
    };

    const handleTouchMove = (e) => {
      // Allow natural touch scrolling/panning in the Spline scene
      // Don't prevent default here to allow Spline camera controls
    };

    const handleTouchEnd = (e) => {
      console.log('👆 Touch end detected on mobile');
    };

    // Add passive touch event listeners for better performance
    const options = { passive: true };
    
    window.addEventListener('touchstart', handleTouchStart, options);
    window.addEventListener('touchmove', handleTouchMove, options);
    window.addEventListener('touchend', handleTouchEnd, options);

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isMobile]);


  // Simple, clean container styles that work everywhere
  const containerStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    overflow: 'hidden',
  };

  // Simple Spline styles - scale slightly on mobile for better fill
  const splineStyle = {
    width: '100vw',
    height: '100vh',
    ...(isMobile && {
      transform: 'scale(1.1)',
      transformOrigin: 'center center',
    }),
  };

  return (
    <div style={containerStyle} className={styles.splineContainer}>
      {/* Background audio */}
      <audio
        ref={audioRef}
        loop
        preload="auto"
        volume={0.3}
        style={{ display: 'none' }}
      >
        <source src="/soothing-deep-noise-293777.m4a" type="audio/mp4" />
        Your browser does not support the audio element.
      </audio>
      
    <Spline 
      scene="https://prod.spline.design/JarpczeEvkS2QYQZ/scene.splinecode" 
      style={splineStyle}
        onLoad={onLoad}
        onSplineMouseDown={onSplineMouseDown}
    />
      
    </div>
  );
}

export default SpaceScene;
